'use client';

import * as React from 'react';

import * as ProgressPrimitive from '@radix-ui/react-progress';
import { motion, useAnimate } from 'framer-motion';
import { cn } from './utils';

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
    duration?: number;
    onCompleted?: (definition: unknown) => void;
    shouldVibrate?: boolean;
    shouldVibrateWhenMax?: boolean;
    extraPercent?: number;
    progressChildrenClassName?: string;
    initialValue?: number;
  }
>(
  (
    {
      children,
      className,
      progressChildrenClassName: progressClassName,
      value = 0,
      max = 100,
      duration,
      onCompleted,
      shouldVibrate = false,
      shouldVibrateWhenMax = false,
      extraPercent = 1,
      initialValue = 0,
      ...props
    },
    ref,
  ) => {
    const [scope, animate] = useAnimate();
    const tunedValue = value ?? 0;
    const percentNum = tunedValue >= max ? (!shouldVibrateWhenMax ? 100 : tunedValue - extraPercent - 0.1) : tunedValue;
    const percent = `${percentNum}%`;

    React.useEffect(() => {
      const animation = async () => {
        await animate(scope.current, { width: `${initialValue}%` });
        await animate(
          scope.current,
          { width: percent },
          {
            ease: 'linear',
            duration: duration ?? 0.6,
          },
        );

        if (onCompleted) {
          onCompleted(scope);
        }
        shouldVibrate &&
          tunedValue > 1 &&
          percentNum <= max &&
          animate(
            scope.current,
            {
              width: percent === '100%' ? percent : `${percentNum + extraPercent}%`,
            },
            {
              repeat: Infinity,
              ease: 'easeOut',
              repeatType: value === 100 ? 'mirror' : 'loop',
              duration: 0.6,
            },
          );
      };

      animation();
    }, [animate, scope, initialValue, value, percent, duration, onCompleted, shouldVibrate, tunedValue, percentNum, max, extraPercent]);

    return (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn('relative h-full w-full overflow-hidden rounded-full bg-secondary', className)}
        {...props}
      >
        <motion.div
          className="h-full"
          initial={false}
          ref={scope}
          style={{ width: `${initialValue}%` }}
          onAnimationComplete={() => {
            onCompleted?.(scope);
          }}
        >
          <ProgressPrimitive.Indicator className={cn('h-full', progressClassName)}>{children}</ProgressPrimitive.Indicator>
        </motion.div>
      </ProgressPrimitive.Root>
    );
  },
);
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
